import { gtagCommand, loadedGtag } from '@/lib/analytics';
import { startConsent } from '@/lib/consent';
import { isInternalDevice, markInternalDevice } from '@/lib/internal-traffic';
import { landingPageFields } from '@/lib/page-view';
import { env } from '@/lib/env';
import type { CurrentUserResponse } from '@/types/generated';
import type { PlanTier } from '@/types/generated/enums';

type Ga4User = Pick<
  CurrentUserResponse,
  'id' | 'managed_spend_band' | 'analytics_internal'
>;

const trafficType = (internal: boolean): 'internal' | null =>
  internal ? 'internal' : null;

// Loaded from code, as on coretas.ai: a GA4 tag in GTM would count every event twice.
export const loadGa4 = (
  measurementId: string | undefined = env.VITE_GA4_MEASUREMENT_ID,
): void => {
  if (!measurementId) return;
  window.gtag = gtagCommand;
  gtagCommand('js', new Date());
  // page-view.ts sends the cleaned page view; the automatic one carries the raw URL.
  gtagCommand('config', measurementId, {
    send_page_view: false,
    ...landingPageFields(),
    ...(isInternalDevice() ? { traffic_type: 'internal' } : {}),
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.append(script);
};

// Before the first route resolve, so the first page view is configured.
export const startAnalytics = (
  measurementId: string | undefined = env.VITE_GA4_MEASUREMENT_ID,
): void => {
  startConsent();
  loadGa4(measurementId);
};

export const identifyGa4User = (user: Ga4User): void => {
  const internal = user.analytics_internal === true;
  if (internal) markInternalDevice();
  const gtag = loadedGtag();
  if (!gtag) return;
  gtag('set', {
    // The user id is personal data: consent mode alone still sends it on a denial.
    user_id: window.CORETAS_CONSENT_GRANTED === true ? String(user.id) : null,
    traffic_type: trafficType(internal || isInternalDevice()),
  });
  gtag('set', 'user_properties', {
    managed_spend_band: user.managed_spend_band ?? null,
  });
};

export const setGa4Plan = (planTier: PlanTier): void => {
  loadedGtag()?.('set', 'user_properties', { plan: planTier.toLowerCase() });
};

export const clearGa4User = (): void => {
  const gtag = loadedGtag();
  if (!gtag) return;
  gtag('set', { user_id: null, traffic_type: trafficType(isInternalDevice()) });
  gtag('set', 'user_properties', { plan: null, managed_spend_band: null });
};
